<!--
// app/components/pages/players-player/TeamSeasonPlayersSlider.vue
-->
<script setup lang="ts">
import type { Player, PlayerSeasons, SeasonPlayerInfo } from "~/src/player/domain/Player";
import type { Team } from "~/src/team/domain/models/Team";
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import CardDraftPlayer from "~/app/components/Shared/players/CardDraftPlayer.vue";
import SliderWrapper from "~/app/components/Shared/SliderWrapper.vue";
import CardPlayerFeaturedCategory from "~/app/components/Shared/players/CardPlayerFeaturedCategory.vue";

const localePath = useLocalePath();
const { region } = useCompetitionStore();

type Props = {
  team: Team | undefined;
  player: PlayerSeasons;
  teamSeasonPlayers: Player[];
  seasonId: number;
  playerSeasonInfo?: SeasonPlayerInfo;
  isMobile?: boolean;
  seasonMvpPartner?: SeasonStatsPartner;
};
const props = defineProps<Props>();

const displayedPlayersCount = computed(() => (props.isMobile ? 3 : 8));
const filteredTeamSeasonPlayers = computed(() => {
  return props.teamSeasonPlayers
    .filter(teamSeasonPlayer => teamSeasonPlayer.id !== props.player.id)
    .slice(0, displayedPlayersCount.value);
});
</script>

<template>
  <div class="pb-12 lg:pb-0 lg:relative" v-if="player && teamSeasonPlayers.length > 0 && seasonId">
    <div class="mx-6 mb-4 lg:mb-0 lg:mx-0">
      <p class="kql-page-section-title">
        {{ $t("player.team.season.players.title") }}
      </p>
      <hr />
    </div>

    <slider-wrapper
      v-if="filteredTeamSeasonPlayers.length"
      :scroll-distance="258"
      scrollable-selector=".players-slider-container"
    >
      <div class="lg:py-12 flex lg:flex-row flex-col lg:gap-4 gap-2 lg:min-w-full overflow-x-auto hide-scrollbar players-slider-container">
        <div
          v-for="teamSeasonPlayer in filteredTeamSeasonPlayers"
          :key="teamSeasonPlayer.id"
          class="lg:w-full lg:min-w-[500px]"
        >
          <card-draft-player
            v-if="
              (teamSeasonPlayer.id !== player.id && teamSeasonPlayer.category === 'draft') ||
              !teamSeasonPlayer.category
            "
            :player-team="team"
            :player-complete-name="teamSeasonPlayer.shortName"
            :player-number="teamSeasonPlayer.jersey"
            :player-position="teamSeasonPlayer.role"
            :player-image="teamSeasonPlayer.image"
            :season-id="seasonId"
            :player-id="teamSeasonPlayer.id"
            :player="teamSeasonPlayer"
            :mvp-partner="seasonMvpPartner"
            isTeamSeasonPlayersSlider
            :show-clause="!!seasonId && seasonId !== 28"
          ></card-draft-player>
          <card-player-featured-category
            v-if="
              teamSeasonPlayer.id !== player.id &&
              teamSeasonPlayer.category &&
              teamSeasonPlayer.category === 'wildcard'
            "
            :player-complete-name="teamSeasonPlayer.shortName"
            :player-number="teamSeasonPlayer.jersey"
            :player-team="team"
            :player-image="teamSeasonPlayer.image"
            :season-id="seasonId"
            :player-category="teamSeasonPlayer.category"
            :player-position="teamSeasonPlayer.role"
            :player-id="teamSeasonPlayer.id"
            :player="teamSeasonPlayer"
            :mvp-partner="seasonMvpPartner"
            isTeamSeasonPlayersSlider
          ></card-player-featured-category>
        </div>
      </div>
    </slider-wrapper>
  </div>
</template>

<style scoped></style>
